import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Tabs,
  Spin,
  Input,
  Dropdown,
  Menu,
  Tag,
  Modal,
  Form,
  Button,
  Select,
  Switch,
  Row,
  Col,
  Statistic,
  Card,
  DatePicker,
} from "antd";
import { useDispatch, useSelector } from "react-redux";
import {
  getAdminWalletHistory,
  getCustomerWallets,
  processWalletRefund,
  processWalletReward,
  getCashbackRules,
  createCashbackRule,
  updateCashbackRule,
  getRewardHistories,
} from "../../../redux/Actions/walletAction";
import { FormatNumber } from "../../../Utils/cartmgt";
import { getUserType } from "../../../redux/Actions/auth";
import { getWalletReducer } from "../../../redux/Reducer/walletReducer";
import { getAuthReducer } from "../../../redux/Reducer/authReducer";
import moment from "moment";
import dayjs from "dayjs";
import { SearchIcon, ViewPersonIcon } from "../../../asset/icons/icons";
import {
  MoreOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  // UndoOutlined,
  GiftOutlined,
  PlusOutlined,
  EditOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { useDebounce } from "../../../Utils/useDebounce";
import { getAllAvailableLocation } from "../../../redux/Actions";

const { Option } = Select;

const usermanText = {
  width: "250px",
  flexShrink: 0,
  color: "#45464E",
  fontFamily: '"MADE TOMMY"',
  fontSize: "20px",
  fontStyle: "normal",
  fontWeight: 500,
  lineHeight: "28px",
  marginBottom: "25px",
};

const tag = {
  padding: "3.867px 7.734px",
  alignItems: "center",
  gap: "0px",
  borderRadius: "348.021px",
  background: "#F4F1FD",
};

const WalletManagement = () => {
  const dispatch = useDispatch();

  // States
  const {
    adminWalletHistory,
    isLoadingAdminWalletHistory,
    adminWalletPagination,
    customerWallets,
    isLoadingCustomerWallets,
    customerWalletsPagination,
    cashbackRules,
    isLoadingRules,
    isSavingRule,
    ruleActionStatus,
    isProcessingRefund,
    isProcessingReward,
    refundStatus,
    rewardStatus,
    rewardHistories,
    isLoadingRewardHistories,
    rewardHistoriesPagination,
    rewardStatistics,
  } = useSelector(getWalletReducer);

  const { userTypeData } = useSelector(getAuthReducer);
  const { availableLocations } = useSelector((state) => state.cartReducer);

  const [activeTab, setActiveTab] = useState("1");
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchText = useDebounce(searchQuery, 500);

  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isRewardModalOpen, setIsRewardModalOpen] = useState(false);
  const [isRuleModalOpen, setIsRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const [refundForm] = Form.useForm();
  const [rewardForm] = Form.useForm();
  const [ruleForm] = Form.useForm();

  const FormatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
    }).format(amount || 0);
  };

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const handleTabChange = (key) => {
    setActiveTab(key);
    setSearchQuery("");
  };

  const fetchData = useCallback(
    (page = 1) => {
      if (activeTab === "1")
        dispatch(getCustomerWallets({ page, search: debouncedSearchText }));
      else if (activeTab === "2")
        dispatch(getAdminWalletHistory({ page, search: debouncedSearchText }));
      else if (activeTab === "3") {
        dispatch(getCashbackRules());
        if (!userTypeData || userTypeData.length === 0) dispatch(getUserType());
        // 👉 ADD THIS: Fetch locations for the dropdown
        if (!availableLocations || availableLocations.length === 0)
          dispatch(getAllAvailableLocation());
      } else if (activeTab === "4")
        dispatch(getRewardHistories({ page, search: debouncedSearchText }));
    },
    [
      dispatch,
      debouncedSearchText,
      activeTab,
      userTypeData,
      availableLocations,
    ],
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const currentCustomerPage = customerWalletsPagination?.current || 1;
  useEffect(() => {
    if (refundStatus) {
      setIsRefundModalOpen(false);
      fetchData(currentCustomerPage);
    }
  }, [refundStatus, fetchData, currentCustomerPage]);

  useEffect(() => {
    if (rewardStatus) {
      setIsRewardModalOpen(false);
      fetchData(currentCustomerPage);
    }
  }, [rewardStatus, fetchData, currentCustomerPage]);

  useEffect(() => {
    if (ruleActionStatus) {
      setIsRuleModalOpen(false);
      ruleForm.resetFields();
      setEditingRule(null);
      dispatch(getCashbackRules());
    }
  }, [ruleActionStatus, ruleForm, dispatch]);

  const onChangePageCustomers = (pagination) =>
    dispatch(
      getCustomerWallets({
        page: pagination.current,
        search: debouncedSearchText,
      }),
    );
  const onChangePageHistory = (pagination) =>
    dispatch(
      getAdminWalletHistory({
        page: pagination.current,
        search: debouncedSearchText,
      }),
    );
  const onChangePageRewards = (pagination) =>
    dispatch(
      getRewardHistories({
        page: pagination.current,
        search: debouncedSearchText,
      }),
    );

  // --- ACTIONS ---
  const handleMenuClick = (e) => {
    if (e.key === "refund") {
      refundForm.resetFields();
      setIsRefundModalOpen(true);
    } else if (e.key === "reward") {
      rewardForm.resetFields();
      setIsRewardModalOpen(true);
    }
  };

  const handleOpenRuleModal = (rule = null) => {
    setEditingRule(rule);
    if (rule) {
      ruleForm.setFieldsValue({
        ...rule,
        is_active: rule.is_active === 1 || rule.is_active === true,
        validity_period:
          rule.start_date && rule.end_date
            ? [
                dayjs(rule.start_date, "YYYY-MM-DD"),
                dayjs(rule.end_date, "YYYY-MM-DD"),
              ]
            : [],
      });
    } else {
      ruleForm.resetFields();
      ruleForm.setFieldsValue({
        is_active: true,
        trigger_type: "amount",
        reward_type: "percentage",
      });
    }
    setIsRuleModalOpen(true);
  };

  const onFinishRefund = (values) => {
    const targetUserId = selectedRecord?.user_id || selectedRecord?.id;
    if (!targetUserId) return;
    dispatch(
      processWalletRefund({
        user_id: parseInt(targetUserId),
        amount: parseFloat(values.amount),
        order_reference: values.order_reference,
      }),
    );
  };

  const onFinishReward = (values) => {
    const targetUserId = selectedRecord?.user_id || selectedRecord?.id;
    if (!targetUserId) return;
    dispatch(
      processWalletReward({
        user_id: parseInt(targetUserId),
        amount: parseFloat(values.amount),
      }),
    );
  };

  const onFinishRule = (values) => {
    const startDate =
      values.validity_period && values.validity_period[0]
        ? values.validity_period[0].format("YYYY-MM-DD")
        : null;
    const endDate =
      values.validity_period && values.validity_period[1]
        ? values.validity_period[1].format("YYYY-MM-DD")
        : null;

    const payload = {
      ...values,
      threshold: parseFloat(values.threshold),
      reward_value: parseFloat(values.reward_value),
      is_active: values.is_active ? 1 : 0,
      start_date: startDate,
      end_date: endDate,
    };
    delete payload.validity_period;

    if (editingRule) dispatch(updateCashbackRule(editingRule.id, payload));
    else dispatch(createCashbackRule(payload));
  };

  const actionMenuItems = (
    <Menu onClick={handleMenuClick}>
      <Menu.Item key="view">
        <div className="d-flex gap-2 align-items-center">
          <ViewPersonIcon /> <span>View Details</span>
        </div>
      </Menu.Item>
      {/* <Menu.Item key="refund">
        <div className="d-flex gap-2 align-items-center text-warning">
          <UndoOutlined /> <span>Issue Refund</span>
        </div>
      </Menu.Item> */}
      <Menu.Item key="reward">
        <div className="d-flex gap-2 align-items-center text-success">
          <GiftOutlined /> <span>Send Reward</span>
        </div>
      </Menu.Item>
    </Menu>
  );

  // --- COLUMNS ---
  const customerColumns = [
    {
      title: "Customer Info",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="orderHistory-text fw-bold">
            {record.user_name || record.name || "N/A"}
          </span>
          <span style={{ fontSize: "12px", color: "#888" }}>
            {record.user_email || record.email}
          </span>
        </div>
      ),
    },
    {
      title: "Wallet ID",
      dataIndex: "uuid",
      render: (text) => (
        <span style={{ fontSize: "12px", color: "#888" }}>{text || "N/A"}</span>
      ),
    },
    {
      title: "Current Balance",
      dataIndex: "balance",
      render: (text) => (
        <span
          className="orderHistory-text fw-bold"
          style={{ fontSize: "16px" }}
        >
          {FormatCurrency(text)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => (
        <Tag
          color={text === "active" ? "green" : "red"}
          style={{ textTransform: "capitalize" }}
        >
          {text || "Unknown"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      render: (text, record) => (
        <Dropdown
          overlay={actionMenuItems}
          trigger={["click"]}
          placement="bottomLeft"
          arrow
          onVisibleChange={(visible) => {
            if (visible) setSelectedRecord(record);
          }}
        >
          <span
            onClick={(e) => e.preventDefault()}
            style={{
              cursor: "pointer",
              padding: "5px 10px",
              border: "1px solid #ddd",
              borderRadius: "5px",
            }}
          >
            Manage <MoreOutlined />
          </span>
        </Dropdown>
      ),
    },
  ];

  // --- HELPER FUNCTIONS FOR TRANSACTION FORMATTING ---
  const getTransactionTypeLabel = (type, source) => {
    const safeType = String(type).toLowerCase();
    const safeSource = String(source).toLowerCase();

    if (safeType === "deposit") {
      if (safeSource === "reward") return "Credit Reward";
      if (safeSource === "refund") return "Refund";
      if (safeSource === "gateway_payment") return "Customer Deposit";
      return "Deposit";
    }
    if (safeType === "debit") {
      if (safeSource === "order_payment") return "Order Payment";
      return "Debit";
    }
    return safeType;
  };

  const getChannelLabel = (source, gateway) => {
    const safeSource = String(source).toLowerCase();
    if (safeSource === "gateway_payment")
      return `Gateway (${gateway || "Unknown"})`;
    if (safeSource === "reward") return "Internal (Reward)";
    if (safeSource === "refund") return "Internal (Refund)";
    if (safeSource === "order_payment") return "Internal (Checkout)";
    return source ? String(source).replace("_", " ") : "System";
  };

  // --- COLUMNS: TRANSACTION HISTORY (FINANCE TEAM VIEW) ---
  const historyColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      render: (text) => (
        <span className="orderHistory-text">
          {moment(text).format("MMM Do YYYY, h:mm A")}
        </span>
      ),
    },
    {
      title: "Customer",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="orderHistory-text fw-bold">{record.user_name}</span>
          <span style={{ fontSize: "12px", color: "#888" }}>
            {record.user_email}
          </span>
        </div>
      ),
    },
    {
      title: "Reference",
      dataIndex: "reference_id",
      render: (text) => (
        <span className="orderHistory-text" style={{ fontSize: "12px" }}>
          {text}
        </span>
      ),
    },
    {
      // 👉 NEW: Channel/Source Column for Finance Reconciliation
      title: "Channel",
      render: (_, record) => (
        <span
          style={{
            textTransform: "capitalize",
            fontSize: "13px",
            color: "#555",
          }}
        >
          {getChannelLabel(record.source, record.payment_gateway)}
        </span>
      ),
    },
    {
      // 👉 UPDATED: Specific Categorization instead of just "Deposit"
      title: "Type",
      render: (_, record) => {
        const isCredit = String(record.type).toLowerCase() === "deposit";
        const label = getTransactionTypeLabel(record.type, record.source);
        return (
          <span
            style={{
              color: isCredit ? "#52c41a" : "#ff4d4f",
              fontWeight: 500,
              textTransform: "capitalize",
            }}
          >
            {isCredit ? (
              <ArrowDownOutlined className="mr-1" />
            ) : (
              <ArrowUpOutlined className="mr-1" />
            )}
            {label}
          </span>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      render: (text, record) => {
        const isCredit = String(record.type).toLowerCase() === "deposit";
        return (
          <span
            className="orderHistory-text fw-bold"
            style={{ color: isCredit ? "#52c41a" : "#ff4d4f" }}
          >
            {isCredit ? "+" : "-"}
            {FormatNumber(text)}
          </span>
        );
      },
    },
    {
      // 👉 UPDATED: Use balance_after to show the exact snapshot in time
      title: "Balance After",
      dataIndex: "balance_after",
      render: (text, record) => (
        <span className="orderHistory-text">
          {FormatNumber(text || record.wallet_balance)}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (text) => {
        let color =
          text === "completed"
            ? "green"
            : text === "pending"
              ? "orange"
              : "red";
        return (
          <Tag color={color} style={{ textTransform: "capitalize" }}>
            {text}
          </Tag>
        );
      },
    },
  ];

  const ruleColumns = [
    {
      title: "Rule Name",
      dataIndex: "name",
      render: (text) => <span className="fw-bold">{text}</span>,
    },
    {
      title: "Target Audience",
      dataIndex: "user_type_id",
      render: (id) => {
        const type = userTypeData?.find((u) => u.id === id);
        return type ? type.name : `Type ${id}`;
      },
    },
    {
      title: "Location",
      dataIndex: "location_id",
      render: (id) => {
        const loc = availableLocations?.find((l) => l.id === id);
        return loc ? loc.name : `Location ${id}`;
      },
    },
    {
      title: "Trigger",
      render: (_, record) => (
        <div style={{ textTransform: "capitalize" }}>
          {record.trigger_type.replace("_", " ")} {" >="}{" "}
          {FormatNumber(record.threshold)}
        </div>
      ),
    },
    {
      title: "Reward",
      render: (_, record) => (
        <Tag color="gold">
          {record.reward_value}
          {record.reward_type === "percentage" ? "%" : " NGN"} Cashback
        </Tag>
      ),
    },
    {
      title: "Validity",
      render: (_, record) => {
        if (!record.start_date || !record.end_date)
          return <span style={{ color: "#888" }}>No expiry</span>;
        return (
          <span style={{ fontSize: "12px", color: "#555" }}>
            {moment(record.start_date).format("MMM Do, YY")}{" "}
            <ArrowRightOutlined style={{ fontSize: "10px", margin: "0 4px" }} />{" "}
            {moment(record.end_date).format("MMM Do, YY")}
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "is_active",
      render: (active) => (
        <Tag color={active ? "green" : "red"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Action",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => handleOpenRuleModal(record)}
          icon={<EditOutlined />}
        >
          Edit
        </Button>
      ),
    },
  ];

  const rewardHistoryColumns = [
    {
      title: "Date",
      dataIndex: "created_at",
      render: (text) => (
        <span className="orderHistory-text">
          {moment(text).format("MMM Do YYYY, h:mm A")}
        </span>
      ),
    },
    {
      title: "Customer",
      render: (_, record) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span className="orderHistory-text fw-bold">{record.user_name}</span>
          <span style={{ fontSize: "12px", color: "#888" }}>
            {record.user_email}
          </span>
        </div>
      ),
    },
    {
      title: "Reward Name",
      dataIndex: "reward_name",
      render: (text) => <Tag color="gold">{text}</Tag>,
    },
    {
      title: "Amount Credited",
      dataIndex: "amount_credited",
      render: (text) => (
        <span
          className="orderHistory-text fw-bold"
          style={{ color: "#52c41a" }}
        >
          +{FormatCurrency(text)}
        </span>
      ),
    },
  ];

  const items = [
    {
      key: "1",
      label: (
        <div style={{ color: "#252A4D" }}>
          Customer Wallets{" "}
          <span className="mx-2" style={tag}>
            {customerWalletsPagination?.total || 0}
          </span>
        </div>
      ),
      children: (
        <div className="row mb-4">
          <Spin spinning={isLoadingCustomerWallets}>
            <Table
              columns={customerColumns}
              dataSource={Array.isArray(customerWallets) ? customerWallets : []}
              onChange={onChangePageCustomers}
              scroll={{ x: 1000 }}
              rowKey="id"
              pagination={{
                current: customerWalletsPagination?.current,
                pageSize: customerWalletsPagination?.pageSize,
                total: customerWalletsPagination?.total,
                showSizeChanger: false,
                style: { display: "flex", justifyContent: "center" },
              }}
            />
          </Spin>
        </div>
      ),
    },
    {
      key: "2",
      label: (
        <div style={{ color: "#252A4D" }}>
          Transaction Logs{" "}
          <span className="mx-2" style={tag}>
            {adminWalletPagination?.total || 0}
          </span>
        </div>
      ),
      children: (
        <div className="row mb-4">
          <Spin spinning={isLoadingAdminWalletHistory}>
            <Table
              columns={historyColumns}
              dataSource={
                Array.isArray(adminWalletHistory) ? adminWalletHistory : []
              }
              onChange={onChangePageHistory}
              scroll={{ x: 1200 }}
              rowKey="id"
              pagination={{
                current: adminWalletPagination?.current,
                pageSize: adminWalletPagination?.pageSize,
                total: adminWalletPagination?.total,
                showSizeChanger: false,
                style: { display: "flex", justifyContent: "center" },
              }}
            />
          </Spin>
        </div>
      ),
    },
    {
      key: "3",
      label: <div style={{ color: "#252A4D" }}>Cashback Rules</div>,
      children: (
        <div className="row mb-4">
          <div className="d-flex justify-content-end mb-3">
            <Button
              type="primary"
              onClick={() => handleOpenRuleModal()}
              icon={<PlusOutlined />}
              style={{ backgroundColor: "#252A4D", height: "40px" }}
            >
              Create New Rule
            </Button>
          </div>
          <Spin spinning={isLoadingRules}>
            <Table
              columns={ruleColumns}
              dataSource={Array.isArray(cashbackRules) ? cashbackRules : []}
              rowKey="id"
              scroll={{ x: 900 }}
              pagination={false}
            />
          </Spin>
        </div>
      ),
    },
    {
      key: "4",
      label: (
        <div style={{ color: "#252A4D" }}>
          Reward History{" "}
          <span className="mx-2" style={tag}>
            {rewardHistoriesPagination?.total || 0}
          </span>
        </div>
      ),
      children: (
        <div className="row mb-4">
          {/* Statistics Cards */}
          <Row gutter={16} className="mb-4">
            <Col span={12}>
              <Card
                bordered={false}
                style={{ background: "#F8F9FD", border: "1px solid #E0E2E7" }}
              >
                <Statistic
                  title="Total Rewards Issued"
                  value={rewardStatistics?.total_rewards || 0}
                />
              </Card>
            </Col>
            <Col span={12}>
              <Card
                bordered={false}
                style={{ background: "#F8F9FD", border: "1px solid #E0E2E7" }}
              >
                <Statistic
                  title="Total Amount Credited"
                  value={rewardStatistics?.total_amount_credited || 0}
                  prefix="₦"
                  precision={2}
                />
              </Card>
            </Col>
          </Row>

          <Spin spinning={isLoadingRewardHistories}>
            <Table
              columns={rewardHistoryColumns}
              dataSource={Array.isArray(rewardHistories) ? rewardHistories : []}
              onChange={onChangePageRewards}
              rowKey="id"
              scroll={{ x: 900 }}
              pagination={{
                current: rewardHistoriesPagination?.current,
                pageSize: rewardHistoriesPagination?.pageSize,
                total: rewardHistoriesPagination?.total,
                showSizeChanger: false,
                style: { display: "flex", justifyContent: "center" },
              }}
            />
          </Spin>
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="p-3 mt-5" style={{ borderRadius: "0px" }}>
        <div className="row">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <span style={usermanText}>Wallet Management</span>
          </div>
        </div>

        {activeTab !== "3" && (
          <div className="d-flex justify-content-between">
            <div className="col-12 col-sm-6 col-md-5">
              <Input
                placeholder={
                  activeTab === "1"
                    ? "Search by Customer Name or Email"
                    : "Search by Reference, Name, or Email"
                }
                style={{
                  width: "100%",
                  border: "none",
                  backgroundColor: "#F7F7F7",
                  height: "44px",
                }}
                value={searchQuery}
                onChange={handleSearch}
                prefix={<SearchIcon />}
              />
            </div>
          </div>
        )}

        <Tabs
          className="border p-2 mt-3 overflow-x-hidden"
          defaultActiveKey="1"
          activeKey={activeTab}
          onChange={handleTabChange}
          items={items}
        />
      </div>

      {/* --- REFUND MODAL --- */}
      <Modal
        title="Process Refund to Wallet"
        open={isRefundModalOpen}
        onCancel={() => setIsRefundModalOpen(false)}
        footer={null}
      >
        <div
          style={{
            padding: "12px",
            backgroundColor: "#F8F9FD",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #E0E2E7",
          }}
        >
          <span style={{ color: "#888", fontSize: "12px" }}>
            Refunding to Customer:
          </span>
          <div
            style={{ fontWeight: "bold", color: "#252A4D", fontSize: "16px" }}
          >
            {selectedRecord?.user_name || selectedRecord?.name}
          </div>
          <div style={{ fontSize: "13px", color: "#555" }}>
            {selectedRecord?.user_email || selectedRecord?.email}
          </div>
        </div>
        <Form layout="vertical" form={refundForm} onFinish={onFinishRefund}>
          <Form.Item
            name="amount"
            label="Refund Amount (NGN)"
            rules={[{ required: true, message: "Amount is required" }]}
          >
            <Input
              type="number"
              placeholder="Enter Amount"
              prefix="₦"
              size="large"
            />
          </Form.Item>
          <Form.Item
            name="order_reference"
            label="Original Order Reference"
            rules={[{ required: true, message: "Order reference is required" }]}
          >
            <Input placeholder="e.g. NB_884933" size="large" />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isProcessingRefund}
            style={{
              backgroundColor: "#252A4D",
              height: "45px",
              fontSize: "16px",
            }}
          >
            Issue Refund
          </Button>
        </Form>
      </Modal>

      {/* --- REWARD MODAL --- */}
      <Modal
        title="Send Wallet Reward"
        open={isRewardModalOpen}
        onCancel={() => setIsRewardModalOpen(false)}
        footer={null}
      >
        <div
          style={{
            padding: "12px",
            backgroundColor: "#F8F9FD",
            borderRadius: "8px",
            marginBottom: "20px",
            border: "1px solid #E0E2E7",
          }}
        >
          <span style={{ color: "#888", fontSize: "12px" }}>
            Rewarding Customer:
          </span>
          <div
            style={{ fontWeight: "bold", color: "#252A4D", fontSize: "16px" }}
          >
            {selectedRecord?.user_name || selectedRecord?.name}
          </div>
          <div style={{ fontSize: "13px", color: "#555" }}>
            {selectedRecord?.user_email || selectedRecord?.email}
          </div>
        </div>
        <Form layout="vertical" form={rewardForm} onFinish={onFinishReward}>
          <Form.Item
            name="amount"
            label="Reward Amount (NGN)"
            rules={[{ required: true, message: "Amount is required" }]}
          >
            <Input
              type="number"
              placeholder="Enter Reward Amount"
              prefix="₦"
              size="large"
            />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isProcessingReward}
            style={{
              backgroundColor: "#52c41a",
              height: "45px",
              borderColor: "#52c41a",
              fontSize: "16px",
            }}
          >
            Send Reward
          </Button>
        </Form>
      </Modal>

      {/* --- CASHBACK RULE MODAL --- */}
      <Modal
        title={editingRule ? "Edit Cashback Rule" : "Create Cashback Rule"}
        open={isRuleModalOpen}
        onCancel={() => setIsRuleModalOpen(false)}
        footer={null}
      >
        <Form layout="vertical" form={ruleForm} onFinish={onFinishRule}>
          <Form.Item name="name" label="Rule Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Distributor Gold Tier" size="large" />
          </Form.Item>
          <div className="row">
            <div className="col-md-6">
              <Form.Item
                name="user_type_id"
                label="Target Audience"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select User Type" size="large">
                  {userTypeData?.slice(2).map((type) => (
                    <Option key={type.id} value={type.id}>
                      {type.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <div className="col-md-6">
              {/* 👉 NEW: Location Dropdown */}
              <Form.Item
                name="location_id"
                label="Target Location"
                rules={[{ required: true }]}
              >
                <Select placeholder="Select Bakery Branch" size="large">
                  {/* Option to apply to all locations, if backend supports null/0 for 'All' */}
                  {/* <Option value={null}>All Locations</Option> */}

                  {availableLocations?.map((loc) => (
                    <Option key={loc.id} value={loc.id}>
                      {loc.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <Form.Item
                name="trigger_type"
                label="Trigger Condition"
                rules={[{ required: true }]}
              >
                <Select size="large">
                  <Option value="amount">Purchase Amount</Option>
                  <Option value="order_count">Order Count</Option>
                </Select>
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="threshold"
                label="Threshold Value"
                rules={[{ required: true }]}
              >
                <Input type="number" size="large" placeholder="e.g. 50000" />
              </Form.Item>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <Form.Item
                name="reward_type"
                label="Reward Type"
                rules={[{ required: true }]}
              >
                <Select size="large">
                  <Option value="percentage">Percentage (%)</Option>
                  <Option value="fixed">Fixed Amount (NGN)</Option>
                </Select>
              </Form.Item>
            </div>
            <div className="col-md-6">
              <Form.Item
                name="reward_value"
                label="Reward Value"
                rules={[{ required: true }]}
              >
                <Input type="number" size="large" placeholder="e.g. 10" />
              </Form.Item>
            </div>
          </div>
          <Form.Item
            name="validity_period"
            label="Validity Period (Start - End)"
          >
            <DatePicker.RangePicker
              style={{ width: "100%" }}
              size="large"
              format="YYYY-MM-DD"
            />
          </Form.Item>
          <Form.Item name="is_active" label="Status" valuePropName="checked">
            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
          </Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            block
            loading={isSavingRule}
            style={{
              backgroundColor: "#252A4D",
              height: "45px",
              fontSize: "16px",
            }}
          >
            {editingRule ? "Update Rule" : "Create Rule"}
          </Button>
        </Form>
      </Modal>
    </>
  );
};

export default WalletManagement;
